import {
    Project,
    ProjectWithDetail,
    ProjectWithLeader,
    ProjectWithTeam,
} from '@/common/types/databaseTypes';
import {
    useMutation,
    useQuery,
    useQueryClient,
    useInfiniteQuery,
} from '@tanstack/react-query';
import { withSafeUsersArray } from '@tickitacka/shared';
import { useSafeQuery } from '@/common/hooks/useSafeQuery';
import { CreateProjectParams, projectService } from './projectService';
import {
    useProjectDeadlineChangedNotification,
    useProjectLeaderChangedNotification,
    useProjectPriorityChangedNotification,
    useProjectStateChangedNotification,
} from '@/features/notification/services/factoryNotificationServices';
import {
    NotificationType,
    UpdateContext,
} from '@/features/notification/constants/notification';
import { useAuthStore } from '@/stores/authStore';
import {
    checkSendNotificationByStatusCode,
    isDifferent,
} from '@/features/notification/utils/notificationUtils';
import { authService } from '@/features/auth/services/authService';
import { useDeleteSchedule } from '@/features/notification/services/useNotificationQueries';
import { workspaceService } from '@/features/workspace/services/workspaceService';
import { CompleteStatusCodes, StatusCode } from '@/common/constants/status';
import { useTeamStore } from '@/stores/teamStore';
import { useTranslation } from 'react-i18next';

// 팀 내 프로젝트 목록 조회 훅 (무한 스크롤 페이징)
export function useProjectsByTeamId(teamId: string, pageSize = 30) {
    const { t } = useTranslation();

    return useInfiniteQuery<ProjectWithLeader[]>({
        queryKey: ['projects', teamId, 'all'],
        queryFn: async ({ pageParam = 0 }) => {
            return await projectService.getProjectsByTeamId(
                teamId,
                pageParam as number,
                pageSize
            );
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.length === pageSize ? allPages.length : undefined;
        },
        enabled: !!teamId, // teamId가 있을 때만 쿼리 실행
        select: data => ({
            ...data,
            pages: data.pages.map(projectsInPage =>
                withSafeUsersArray(projectsInPage, t, {
                    userFields: ['leader'],
                })
            ),
        }),
    });
}

// 프로젝트 공유받고 있는 팀들의 프로젝트 목록 조회 훅
export function useSharedProjects(teamId: string) {
    return useSafeQuery<
        { projects: ProjectWithTeam[]; sharedProjectIds: string[] },
        Error
    >({
        queryKey: ['projects', 'shared', teamId],
        queryFn: () => projectService.getSharedProjects(teamId),
        enabled: !!teamId, // teamId가 있을 때만 쿼리 실행
    });
}

// 프로젝트 상세 정보 조회 훅
export function useProjectDetailsById(projectId: string) {
    return useSafeQuery<ProjectWithDetail, Error>({
        queryKey: ['project', projectId, 'detail'],
        queryFn: () => projectService.getProjectDetailsById(projectId),
        enabled: !!projectId, // projectId가 있을 때만 쿼리 실행
    });
}

// 단일 프로젝트 조회 훅
export function useProject(projectId: string) {
    return useSafeQuery<Project, Error>({
        queryKey: ['project', projectId],
        queryFn: () => projectService.getProjectById(projectId),
        enabled: !!projectId, // projectId가 있을 때만 쿼리 실행
    });
}

// 프로젝트 생성 뮤테이션 훅
export function useCreateProject() {
    const queryClient = useQueryClient();

    return useMutation<Project, Error, CreateProjectParams>({
        mutationFn: async newProject =>
            await projectService.createProject(newProject),
        onSuccess: async () => {
            // 프로젝트 목록 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
    });
}

export interface UpdateProjectOptions {
    skipInvalidation?: boolean;
}

// 프로젝트 수정 뮤테이션 훅
export function useUpdateProject(options?: UpdateProjectOptions) {
    const queryClient = useQueryClient();

    // 알림 발송 함수 참조
    const { createProjectPriorityChangedNotification } =
        useProjectPriorityChangedNotification();
    const { createProjectStateChangedNotification } =
        useProjectStateChangedNotification();
    const { createProjectDeadlineChangedNotification } =
        useProjectDeadlineChangedNotification();
    const { createProjectLeaderChangedNotification } =
        useProjectLeaderChangedNotification();
    const deleteSchedule = useDeleteSchedule();

    return useMutation<
        Project,
        Error,
        { id: string } & Partial<Project>,
        UpdateContext<Project>
    >({
        mutationFn: updatedProject =>
            projectService.updateProject(updatedProject.id, updatedProject),

        onMutate: async variables => {
            // 이전 데이터 가져오기
            const oldProjectData = await projectService.getProjectById(
                variables.id
            );

            // 변경 유형 확인
            const changeTypes = {
                isPriorityChange: isDifferent(
                    variables.priority_code,
                    oldProjectData.priority_code
                ),
                isStatusChange: isDifferent(
                    variables.status_code,
                    oldProjectData.status_code
                ),
                isDeadlineChange:
                    isDifferent(
                        variables.start_date,
                        oldProjectData.start_date
                    ) ||
                    isDifferent(variables.end_date, oldProjectData.end_date),
                isLeaderChange: isDifferent(
                    variables.leader_id,
                    oldProjectData.leader_id
                ),
            };

            return {
                oldData: oldProjectData,
                changeTypes,
            };
        },

        onSuccess: async (updatedProject, variables, context) => {
            // 개별 프로젝트와 프로젝트 목록 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['projects'] });
            if (!options?.skipInvalidation) {
                await queryClient.invalidateQueries({
                    queryKey: ['project', updatedProject.id],
                });
            }

            // 목표 목록 캐시 무효화
            await queryClient.invalidateQueries({
                queryKey: ['goal', 'team'],
            });

            // 변경 관련 알림 발송
            if (!context || !context.oldData) {
                console.log(
                    'Failed to send project update notification: context or oldData is undefined'
                );
                return;
            }

            // 알림 발송 조건 체크 - Active 상태인 프로젝트거나 상태 변경일 경우에만 알림 발송
            if (
                !checkSendNotificationByStatusCode(
                    updatedProject,
                    context.changeTypes
                )
            ) {
                return;
            }

            try {
                const { user } = useAuthStore.getState();

                // 완료 상태로 변경 시, 알림 스케줄링 데이터 삭제
                if (
                    context.changeTypes.isStatusChange &&
                    CompleteStatusCodes.includes(
                        updatedProject.status_code as StatusCode
                    )
                ) {
                    deleteSchedule.mutate({
                        relatedType: 'project',
                        relatedId: updatedProject.id,
                        notificationType:
                            NotificationType.PROJECT_DEADLINE_REMINDER,
                    });
                }

                // 프로젝트 참여자 정보 가져오기
                const participantIds =
                    await projectService.getProjectParticipantsByProjectId(
                        updatedProject.id
                    );

                // 관리자는 포함, 업데이트 행위자는 제외
                const relatedUserIds = [
                    ...new Set([...participantIds, updatedProject.leader_id]),
                ];

                // 워크스페이스 멤버십 확인을 비동기로 수행
                const membershipChecks = await Promise.all(
                    relatedUserIds.map(async userId => {
                        try {
                            // 워크스페이스 멤버십 권한 확인
                            const membershipData =
                                await workspaceService.checkUserWorkspaceMembership(
                                    updatedProject.team_id,
                                    userId
                                );
                            return {
                                userId,
                                isAllowed: membershipData.isAllowed,
                            };
                        } catch (error) {
                            console.error(
                                `워크스페이스 멤버십 확인 실패 (유저 ID: ${userId}):`,
                                error
                            );
                            return { userId, isAllowed: false };
                        }
                    })
                );

                // 워크스페이스 멤버만 필터링
                const uniqueParticipantIds = membershipChecks
                    .filter(check => check.isAllowed)
                    .map(check => check.userId);

                const sendNotiUserIds = uniqueParticipantIds.filter(
                    userId => userId !== user?.id
                );

                const { changeTypes } = context;
                const notificationPromises = [];
                const schedulePromises = [];
                const projectId = updatedProject.id;

                // 공통 데이터
                const commonData = {
                    project_title: updatedProject.name,
                    changer_name: user.name,
                    changer_image_url: user.profile_image_url,
                    changer_id: user.id,
                    team_id: updatedProject.team_id,
                };

                // 1. 우선순위 변경 알림
                if (changeTypes.isPriorityChange && variables.priority_code) {
                    notificationPromises.push(
                        ...sendNotiUserIds.map(userId =>
                            createProjectPriorityChangedNotification({
                                userId,
                                projectId,
                                projectPriorityChangedContent: {
                                    ...commonData,
                                    old_priority: context.oldData.priority_code,
                                    new_priority: variables.priority_code,
                                },
                            })
                        )
                    );
                }

                // 2. 상태 변경 알림
                if (changeTypes.isStatusChange && variables.status_code) {
                    notificationPromises.push(
                        ...sendNotiUserIds.map(userId =>
                            createProjectStateChangedNotification({
                                userId,
                                projectId,
                                projectStateChangedContent: {
                                    ...commonData,
                                    old_state: context.oldData.status_code,
                                    new_state: variables.status_code,
                                },
                            })
                        )
                    );
                }

                // 3. 마감기한 변경 알림
                if (
                    changeTypes.isDeadlineChange &&
                    (variables.start_date || variables.end_date)
                ) {
                    notificationPromises.push(
                        ...sendNotiUserIds.map(userId =>
                            createProjectDeadlineChangedNotification({
                                userId,
                                projectId,
                                projectDeadlineChangedContent: {
                                    ...commonData,
                                    old_start_date: context.oldData.start_date,
                                    old_end_date: context.oldData.end_date,
                                    new_start_date: variables.start_date,
                                    new_end_date: variables.end_date,
                                },
                            })
                        )
                    );

                    // 알림 스케줄링 삭제 - 새 스케줄 추가는 매시간 실행되는 rpc 함수로 처리
                    deleteSchedule.mutate({
                        relatedType: 'project',
                        relatedId: updatedProject.id,
                        notificationType:
                            NotificationType.PROJECT_DEADLINE_REMINDER,
                    });
                }

                // 4. 관리자 변경 알림 - 해당 관리자에게만 전송
                if (changeTypes.isLeaderChange && variables.leader_id) {
                    const oldLeader = await authService.getUserById(
                        context.oldData.leader_id
                    );
                    const newLeader = await authService.getUserById(
                        variables.leader_id
                    );

                    if (
                        oldLeader &&
                        newLeader &&
                        oldLeader.id !== newLeader.id
                    ) {
                        notificationPromises.push(
                            createProjectLeaderChangedNotification({
                                userId: newLeader.id,
                                projectId,
                                projectLeaderChangedContent: {
                                    ...commonData,
                                    old_leader_name: oldLeader.name,
                                    old_leader_id: oldLeader.id,
                                    new_leader_name: newLeader.name,
                                    new_leader_id: newLeader.id,
                                },
                            })
                        );
                    }
                }

                // 모든 알림 한 번에 처리
                await Promise.all(notificationPromises);
                await Promise.all(schedulePromises);
            } catch (error) {
                console.error('알림 발송 또는 스케줄 등록 실패:', error);
                // 주요 업데이트는 이미 성공했으므로 에러를 무시
            }
        },
    });
}

// 프로젝트 삭제 뮤테이션 훅
export function useDeleteProject() {
    const queryClient = useQueryClient();
    const deleteNotificationSchedule = useDeleteSchedule();

    return useMutation<void, Error, string>({
        mutationFn: projectId => projectService.deleteProject(projectId),
        onSuccess: async (_, projectId) => {
            // 프로젝트 목록 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['projects'] });

            // 프로젝트 마감기한 알림 스케줄 삭제
            try {
                await deleteNotificationSchedule.mutateAsync({
                    relatedType: 'project',
                    relatedId: projectId,
                    notificationType:
                        NotificationType.PROJECT_DEADLINE_REMINDER,
                });
            } catch (error) {
                console.error('Failed to delete notification schedule:', error);
            }
        },
    });
}

// 프로젝트 공유 상태 업데이트 뮤테이션 훅 (공유 또는 공유 취소)
export function useUpdateProjectSharing() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            projectId,
            teamId,
            enable,
        }: {
            projectId: string;
            teamId: string;
            enable: boolean;
        }) => projectService.updateProjectSharing(projectId, teamId, enable),
        onSuccess: (_, variables) => {
            // 프로젝트 공유 상태 변경 후 관련 데이터 갱신
            void queryClient.invalidateQueries({
                queryKey: ['projects', variables.teamId],
            });
            void queryClient.invalidateQueries({
                queryKey: ['project', variables.projectId, 'detail'],
            });
            void queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
    });
}

// 프로젝트 공유 상태 확인 뮤테이션 훅
export function useCheckProjectSharing() {
    return useMutation({
        mutationFn: ({
            projectId,
            teamId,
        }: {
            projectId: string;
            teamId: string;
        }) => projectService.checkProjectSharingStatus(projectId, teamId),
    });
}

// 통합된 변수 타입
export interface UpdateProjectParticipantVariables {
    action: 'add' | 'remove';
    projectId: string;
    userId: string;
    registerId?: string; // add 액션에만 필요
}

// 사용자가 참여하고 있는 프로젝트 목록 조회 훅
export function useMyProjects({ workspaceId }: { workspaceId?: string }) {
    const { user } = useAuthStore.getState();
    return useSafeQuery({
        queryKey: ['projects', user?.id, workspaceId],
        queryFn: () => projectService.getMyProjects(user.id, { workspaceId }),
        enabled: !!user?.id,
    });
}

export function useUpdateProjectParticipant() {
    const queryClient = useQueryClient();
    const currentTeam = useTeamStore.getState().currentTeam;
    const { user } = useAuthStore.getState();

    return useMutation<boolean, Error, UpdateProjectParticipantVariables>({
        mutationFn: async ({ action, projectId, userId, registerId }) => {
            if (action === 'add' && registerId) {
                return projectService.addProjectParticipant(
                    projectId,
                    userId,
                    registerId
                );
            } else if (action === 'remove') {
                // registerId는 현재 사용자 ID 사용 (제거 작업을 수행하는 사용자)
                const currentUserId = user?.id || '';
                return projectService.removeProjectParticipantWithDependencies(
                    projectId,
                    userId,
                    currentUserId
                );
            }
            throw new Error(
                '유효하지 않은 액션이거나 필요한 매개변수가 누락되었습니다'
            );
        },
        onSuccess: async (_, variables) => {
            // 프로젝트 관련 쿼리 무효화
            await queryClient.invalidateQueries({
                queryKey: ['project', variables.projectId],
            });
            await queryClient.invalidateQueries({
                queryKey: ['project', variables.projectId, 'detail'],
            });

            // 팀 프로젝트 목록 무효화
            await queryClient.invalidateQueries({
                queryKey: ['project', 'team', currentTeam?.id],
            });

            // 사용자가 참여하고 있는 프로젝트 목록 무효화
            await queryClient.invalidateQueries({
                queryKey: ['projects', user?.id],
            });

            // goal 관련 쿼리들도 무효화 (참여자 제거 시 goal 데이터가 변경될 수 있음)
            await queryClient.invalidateQueries({
                queryKey: ['goal', 'team', currentTeam?.id],
            });

            // todo 관련 쿼리들도 무효화 (goal_id가 null로 변경될 수 있음)
            await queryClient.invalidateQueries({
                queryKey: ['todo'],
            });
        },
        onError: error => {
            // 에러 로깅 및 사용자에게 알림
            const actionType = error.message.includes('추가') ? '추가' : '제거';
            console.error(`프로젝트 참여자 ${actionType} 실패:`, error);
        },
    });
}

// 프로젝트 참여자 ID 조회 훅
export function useProjectParticipantIds(projectId: string | undefined) {
    return useQuery({
        queryKey: ['project', projectId, 'participants'],
        queryFn: () =>
            projectService.getProjectParticipantsByProjectId(projectId),
        enabled: !!projectId,
    });
}
